/**
 * The call path every Edge Function invocation in the app goes through.
 *
 * This is hand-written. It sits in its own module rather than inside
 * `client.ts` so that the retry and session-recovery logic reads as maintained
 * code instead of as something regenerable sitting next to the Supabase
 * singleton. `client.ts` re-exports it, which is still where nearly every call
 * site imports it from.
 *
 * On the return type and `TData = any`: this used to be annotated
 * `Promise<FunctionsResponse<TData>>`, with `FunctionsResponse` imported from
 * `@supabase/supabase-js` -- which does not export it. It lives in
 * `@supabase/functions-js`. The import never resolved, so the annotation
 * collapsed to `any` and every one of the ~120 call sites has been reading
 * Edge Function responses unchecked ever since.
 *
 * The annotation is now inferred from `client.functions.invoke` itself, so it
 * cannot drift from the SDK and needs no dependency on an internal package.
 * `any` stays as the default type argument because it is what the call sites
 * have always been getting; it now says so out loud instead of hiding behind a
 * broken import. Narrowing it to `unknown` is the right end state, but it
 * surfaces 146 real type errors across ~60 files, so it needs its own change.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { clearSessionIfServerRejectsIt } from '@/utils/staleSession';

const FUNCTION_RETRY_DELAYS_MS = [300, 600, 1200] as const;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getInvokeErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;

  const maybeStatus = (error as { status?: unknown }).status;
  if (typeof maybeStatus === 'number') return maybeStatus;

  const contextStatus = (error as { context?: { status?: unknown } }).context?.status;
  if (typeof contextStatus === 'number') return contextStatus;

  return undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- deliberate: see the note at the top of this file.
export async function invokeFunction<TData = any, TBody = unknown>(
  client: SupabaseClient,
  functionName: string,
  options?: { body?: TBody } & Record<string, unknown>,
) {
  let firstRetryableError: unknown;

  for (let attempt = 0; attempt <= FUNCTION_RETRY_DELAYS_MS.length; attempt += 1) {
    const response = await client.functions.invoke<TData>(functionName, options);
    if (!response.error) return response;

    const status = getInvokeErrorStatus(response.error);
    // A 401 here, or a 403 from a guard that read the caller as anonymous, may
    // be a session the server has forgotten rather than a denial; clearing it
    // sends the app to sign-in instead of letting every later call fail the
    // same way. See src/utils/staleSession.ts.
    if (status === 401 || status === 403) {
      await clearSessionIfServerRejectsIt(client);
    }
    if (status !== 503) {
      throw response.error;
    }

    firstRetryableError ??= response.error;
    if (attempt === FUNCTION_RETRY_DELAYS_MS.length) {
      throw firstRetryableError;
    }

    await sleep(FUNCTION_RETRY_DELAYS_MS[attempt]);
  }

  throw new Error('Unreachable invokeFunction retry state');
}
